import type {
  CreateAccountRequest,
  GetAccountResponse,
  ListAccountsResponse,
  ListBrokerOrderRecordsResponse,
  ListPositionsResponse,
  ListProfitsResponse,
  ListTransactionsResponse,
} from '@fleece/models';
import { useInfiniteQuery, useIsFetching, useMutation, useQuery, useQueryClient, type InfiniteData, type UseInfiniteQueryResult, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useApi } from '@/hooks/use-connection';
import { FIRST_TRANSACTION_CURSOR, nextTransactionCursor, transactionLimit, transactionPage, type TransactionCursor, type TransactionPage } from '@/lib/ledger';

/** Every query on these pages starts with this, so one invalidation refreshes them all. */
const LEDGER = 'ledger';

export function useAccounts(): UseQueryResult<ListAccountsResponse> {
  const api = useApi();
  return useQuery({ queryKey: [LEDGER, 'accounts'], queryFn: () => api.listAccounts({}) });
}

/** Creates an account, then reads the list again, so it shows what the service stored rather than what was sent. */
export function useCreateAccount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateAccountRequest) => api.createAccount(request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [LEDGER, 'accounts'] }),
  });
}

export function useAccount(accountId: string): UseQueryResult<GetAccountResponse> {
  const api = useApi();
  return useQuery({ queryKey: [LEDGER, 'account', accountId], queryFn: () => api.getAccount({ accountId }) });
}

/**
 * Open positions only, unless `includeClosed`: a closed position stays in the ledger at
 * size zero, so an account that has traded for a year has far more of those than open ones.
 */
export function usePositions(accountId: string, includeClosed: boolean): UseQueryResult<ListPositionsResponse> {
  const api = useApi();
  return useQuery({ queryKey: [LEDGER, 'positions', accountId, includeClosed], queryFn: () => api.listPositions({ accountId, includeClosed }) });
}

export function useProfits(accountId: string): UseQueryResult<ListProfitsResponse> {
  const api = useApi();
  return useQuery({ queryKey: [LEDGER, 'profits', accountId], queryFn: () => api.listProfits({ accountId }) });
}

/** An account's transactions, newest first, a page at a time; `symbol` narrows them to one. */
export function useTransactions(accountId: string, symbol: string | undefined): UseInfiniteQueryResult<InfiniteData<TransactionPage, TransactionCursor>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [LEDGER, 'transactions', accountId, symbol ?? null],
    initialPageParam: FIRST_TRANSACTION_CURSOR,
    queryFn: async ({ pageParam }): Promise<TransactionPage> => {
      const limit = transactionLimit(pageParam);
      const response = await api.listTransactions({ accountId, symbol, from: pageParam.from, limit, sort: 'desc' });
      return transactionPage(response.transactions, pageParam, limit);
    },
    getNextPageParam: (lastPage, pages) =>
      nextTransactionCursor(
        pages.flatMap((page) => page.transactions),
        lastPage.full,
      ),
  });
}

/** The account's most recent transaction in any symbol: when it last traded, whatever the log below is narrowed to. */
export function useLatestTransaction(accountId: string): UseQueryResult<ListTransactionsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: [LEDGER, 'latest-transaction', accountId],
    queryFn: () => api.listTransactions({ accountId, from: FIRST_TRANSACTION_CURSOR.from, limit: 1, sort: 'desc' }),
  });
}

/**
 * Every event the broker sent about one order, oldest first, each recorded once however
 * often it was delivered. A 404 means no broker order is recorded under that id — a fill booked directly
 * rather than through order tracking.
 */
export function useOrderRecords(brokerOrderId: string): UseQueryResult<ListBrokerOrderRecordsResponse> {
  const api = useApi();
  return useQuery({ queryKey: [LEDGER, 'order-records', brokerOrderId], queryFn: () => api.listBrokerOrderRecords({ brokerOrderId }) });
}

export interface LedgerRefresh {
  readonly refresh: () => void;
  /** True while any ledger query is in flight, whatever started it. */
  readonly refreshing: boolean;
}

/** Reads every ledger figure on screen again: positions, profits and the log move with each fill. */
export function useLedgerRefresh(): LedgerRefresh {
  const queryClient = useQueryClient();
  const refreshing = useIsFetching({ queryKey: [LEDGER] }) > 0;
  const refresh = useCallback(() => void queryClient.invalidateQueries({ queryKey: [LEDGER] }), [queryClient]);
  return { refresh, refreshing };
}
